import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button, Card } from '../ui/kit'
import { useI18n } from '../i18n'
import { useAuth } from '../auth'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { kaufknopfSichtbar, kaufweg, type PayChannel } from '../lib/kaufweg'
import { StaySuiteStore, type StoreProdukt } from '../lib/staysuiteStore'
import {
  kaufwegOffen,
  ladenAboVerwalten,
  ladenAnspruchMelden,
  ladenKaufen,
  ladenPreis,
  ladenProdukte,
} from '../data/ladenAbo'
import { billingStage, hasUsableMandate, loadBillingState, type BillingState } from '../data/stripe'

/**
 * Das Laden-Kaufblatt der Hülle (Vorgang 009, Schnitt 8 — Apple zuerst).
 *
 * Was hier NICHT steht, ist die halbe Entscheidung:
 *   - Kein Preis aus `src/` — der Betrag ist Apples `displayPrice` (AK32).
 *   - Kein Verweis auf www.staysuite.app, Stripe oder einen Kauf außerhalb
 *     der App (AU1, 3.1.1).
 *   - Kein Kaufknopf für Nicht-Inhaber: die Sperre greift VOR dem Kaufblatt —
 *     ein Kaufblatt, das erst nach dem Bezahlen „Sie durften das nicht" sagt,
 *     ist eine Erstattungsanfrage mit Anlauf (AK16/AU2).
 *   - Finish ausschließlich nach serverbestätigter Wirkung; Beobachtung bleibt offen.
 *
 * Der Kaufknopf erscheint nur, wenn zusätzlich der SERVER `kaufweg_offen`
 * meldet (X9/A8.6): ein Rückzug ist eine Einstellung, kein Store-Release —
 * der Knopf verschwindet beim nächsten App-Start (AX1/AX2).
 *
 * „Abo verwalten" öffnet Apples eigenes Verwaltungsblatt: die Kündigung eines
 * Apple-Abos geschieht ausschließlich bei Apple (iOS-Einstellungen → Abos) —
 * die App zeigt den Weg, statt ihn zu verstecken (Renés Frage vom 19.08.2026;
 * dieselbe Logik wie `kontoLoeschung.aboApple` aus Vorgang 019). Es gibt
 * keinen eigenen Kündigungs-RPC: `cancelled_pending` kommt über `apple-notify`
 * herein und wirkt zum Periodenende (`abo_laden_zustaende`).
 */
function kanalAusBilling(b: BillingState | null): PayChannel {
  // Stripe ist beim neuen Testkonto der Datenbankdefault, noch kein bezahlter Kanal.
  if (b?.pay_channel === 'stripe' && b.status === 'trialing' &&
      !hasUsableMandate(b) && b.mandate_status !== 'requested') return null
  return b?.pay_channel ?? null
}

export default function LadenAboView() {
  const { t, formatDate } = useI18n()
  const { tenantId, rolle } = useAuth()
  const { reload } = useStore()
  const [billingGeladen, setBillingGeladen] = useState(false)

  const [offen, setOffen] = useState<boolean | null>(null)
  const [produkte, setProdukte] = useState<StoreProdukt[]>([])
  const [ladeFehler, setLadeFehler] = useState(false)
  const [billing, setBilling] = useState<BillingState | null>(null)
  const [appleKaufDa, setAppleKaufDa] = useState(false)
  const [umgebung, setUmgebung] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Nur der GEKLICKTE Knopf zeigt den Spinner; ein globales busy ließ beim
  // ersten Sandkasten-Kauf (20.08.2026) alle drei Knöpfe gleichzeitig kreisen.
  const [busyProdukt, setBusyProdukt] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  // Erscheint dieses Blatt, obwohl der Kaufweg nicht live ist, ist es der
  // Sandkasten-Prüfbuild — das wird angezeigt, nicht versteckt (AS3).
  const pruefbuild = kaufweg() !== 'apple'
  const sandkasten = umgebung === 'Sandbox' || umgebung === 'Xcode'

  useEffect(() => {
    let aktiv = true
    setBillingGeladen(false)
    void (async () => {
      const o = await kaufwegOffen()
      if (aktiv) setOffen(o)
      try {
        const { produkte: rows } = await ladenProdukte()
        if (aktiv) {
          // StoreKit liefert die Produkte in beliebiger Ladenreihenfolge —
          // beim ersten Sandkasten-Kauf stand das mittlere Angebot zuoberst.
          // Sortiert wird nach dem LADENBETRAG, nie nach einer Zahl aus src/.
          setProdukte([...rows].sort((a, b) => a.betragMikro - b.betragMikro))
          setLadeFehler(rows.length === 0)
        }
      } catch {
        if (aktiv) setLadeFehler(true)
      }
      try {
        const antwort = await StaySuiteStore.aktiveKaeufe()
        const kauf = (antwort.kaeufe ?? []).find((k) => k.jws)
        if (aktiv && kauf) {
          setAppleKaufDa(true)
          setUmgebung(kauf.umgebung ?? null)
        }
      } catch {
        /* kein Laden erreichbar — das Blatt bleibt benutzbar (AS7) */
      }
      if (supabase && tenantId) {
        try {
          const b = await loadBillingState(supabase, tenantId)
          if (aktiv) { setBilling(b); setBillingGeladen(true) }
        } catch {
          /* Abo-Zeile nicht lesbar — dann entscheidet der Server beim Kauf */
        }
      }
    })()
    return () => { aktiv = false }
  }, [tenantId])

  async function kaufen(produktId: string) {
    if (!tenantId) return
    setBusy(true)
    setBusyProdukt(produktId)
    setMeldung(null)
    try {
      const { kauf, server, fehler, zustand } = await ladenKaufen(produktId, tenantId)
      if (kauf?.umgebung) setUmgebung(kauf.umgebung)
      if (kauf?.jws) setAppleKaufDa(true)
      const ergebnis = String(server?.ergebnis ?? server?.error ?? '')
      if (ergebnis === 'gehoert_anderem_betrieb') setMeldung(t('abo.laden.fremderBetrieb'))
      else if (ergebnis === 'keine_berechtigung' || ergebnis === 'waise') {
        setMeldung(t('abo.laden.erstattungBeimLaden'))
      } else if (zustand === 'cancelled') setMeldung(t('abo.laden.abgebrochen'))
      else if (zustand === 'pending') setMeldung(t('abo.laden.ausstehend'))
      else if (zustand === 'active') {
        setMeldung(t('abo.laden.aktiviert'))
        reload()
        if (supabase) setBilling(await loadBillingState(supabase, tenantId))
      } else if (fehler) setMeldung(t('abo.laden.kaufFehler'))
      else setMeldung(t('abo.laden.gemeldet'))
    } catch {
      setMeldung(t('abo.laden.kaufFehler'))
    } finally {
      setBusy(false)
      setBusyProdukt(null)
    }
  }

  async function wiederherstellen() {
    if (!tenantId) return
    setBusy(true)
    setMeldung(null)
    try {
      const ergebnis = await ladenAnspruchMelden(tenantId, undefined, true)
      setMeldung(t(ergebnis.zustand === 'active' ? 'abo.laden.aktiviert'
        : ergebnis.zustand === 'observed' ? 'abo.laden.gemeldet'
        : ergebnis.zustand === 'empty' ? 'abo.laden.keinKauf' : 'abo.laden.kaufFehler'))
      if (ergebnis.zustand === 'active') {
        reload()
        if (supabase) setBilling(await loadBillingState(supabase, tenantId))
      }
      // Frisch gemeldete Ansprüche sofort zeigen — sonst stünden die
      // Kaufknöpfe bis zum nächsten Blattbesuch unter einem laufenden Abo.
      const antwort = await StaySuiteStore.aktiveKaeufe()
      const kauf = (antwort.kaeufe ?? []).find((k) => k.jws)
      if (kauf) {
        setAppleKaufDa(true)
        setUmgebung(kauf.umgebung ?? null)
      }
    } catch {
      setMeldung(t('abo.laden.kaufFehler'))
    } finally {
      setBusy(false)
    }
  }

  /*
   * AU3: bezahlt dieser Betrieb bereits über den Browserweg, gibt es keinen
   * Kaufknopf und einen Satz OHNE Preis und OHNE Verweis nach draußen.
   * Die Kanalachse kommt aus pay_channel; ein unfinanziertes neues Testkonto
   * trägt dort lediglich den Datenbankdefault stripe.
   */
  const payChannel = kanalAusBilling(billing)
  const andererKanal = payChannel !== null && payChannel !== 'apple'
  /*
   * Die vierteilige Entscheidung liegt als REINE Funktion in lib/kaufweg.ts
   * und ist dort über alle Kombinationen geprüft (AK47). Dieses Blatt IST der
   * Apple-Weg (Route nur in der iOS-Hülle); im Sandkasten-Prüfbuild sagt
   * `kaufweg()` zwar 'keiner', aber das ist die Frage nach der
   * Build-Konstante — die Sichtbarkeit des Blatts entscheidet
   * `ladenKaufwegSichtbar()` am Router, hier zählt der Laden dieses Blatts.
   */
  const darfKaufen = billingGeladen && !ladeFehler && kaufknopfSichtbar('apple', offen === true, rolle, payChannel)

  // AU2 verlangt den Abo-Zustand SICHTBAR, auch für Nicht-Inhaber — grob,
  // ohne Betrag: die feinen Ladenzustände kommen erst mit 015/2b (AP9).
  const stufeAbo = billing ? billingStage(billing) : null
  const zustandZeile =
    stufeAbo === 'active' || stufeAbo === 'trial_mandate_ok'
      ? t('abo.laden.zustandAktiv')
      : stufeAbo === 'trial_no_mandate' || stufeAbo === 'trial_pending'
        ? t('abo.laden.zustandTest', {
            datum: billing?.trial_ends_at ? formatDate(billing.trial_ends_at) : '—',
          })
        : stufeAbo !== null
          ? t('abo.laden.zustandLesend')
          : null

  return (
    <main className="p-4">
      <div className="mx-auto max-w-2xl space-y-4">
        <header>
          <h1 className="text-2xl font-semibold">{t('abo.laden.titel')}</h1>
          {(sandkasten || pruefbuild) && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-warn">
              {t(sandkasten ? 'abo.laden.sandkasten' : 'abo.laden.pruefbuild')}
            </p>
          )}
        </header>

        {/* AU2: der Abo-Zustand steht für JEDE Rolle da, vor allen Sperren. */}
        {zustandZeile && <Card className="p-4 text-sm">{zustandZeile}</Card>}

        {andererKanal && (
          <Card className="p-4 text-sm text-muted">{t('abo.laden.andererKanal')}</Card>
        )}
        {!andererKanal && rolle !== 'owner' && (
          <Card className="p-4 text-sm text-muted">{t('abo.laden.nurInhaber')}</Card>
        )}

        {/* Ein auf DIESEM Gerät stehender Apple-Anspruch ersetzt die
            Kaufknöpfe: Planwechsel und Kündigung laufen dann über den App
            Store („Abo verwalten"), ein zweiter Abschluss derselben Gruppe
            wäre nur ein Umweg dorthin. subscriptions weiß im off-Modus
            (A8.4) noch nichts davon — die Quelle ist aktiveKaeufe(). */}
        {darfKaufen && appleKaufDa && (
          <Card className="p-4 text-sm">{t('abo.laden.kaufVorhanden')}</Card>
        )}
        {darfKaufen && !appleKaufDa && (
          <Card className="space-y-3 p-4">
            {ladeFehler && <p className="text-sm text-danger">{t('abo.laden.ladeFehler')}</p>}
            {produkte.map((p) => (
              <div key={p.produktId} className="flex items-center gap-3 rounded-xl border border-border p-3">
                <span className="min-w-0 flex-1">
                  {/* Apples lokalisierter Betrag — die einzige Preisquelle (AK32). */}
                  <strong className="block">{ladenPreis(p)}</strong>
                  <span className="block text-sm">{p.name ?? p.produktId}</span>
                  {p.beschreibung && <span className="block text-xs text-muted">{p.beschreibung}</span>}
                </span>
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => void kaufen(p.produktId)}
                >
                  {busyProdukt === p.produktId
                    ? <Loader2 size={16} className="animate-spin" />
                    : t('abo.laden.kaufen')}
                </Button>
              </div>
            ))}
          </Card>
        )}

        <Card className="space-y-3 p-4">
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || !tenantId} onClick={() => void wiederherstellen()}>
              {t('abo.laden.wiederherstellen')}
            </Button>
            {appleKaufDa && (
              <Button disabled={busy} onClick={() => void ladenAboVerwalten().catch(() => setMeldung(t('abo.laden.kaufFehler')))}>
                {t('abo.laden.verwalten')}
              </Button>
            )}
          </div>
          {appleKaufDa && (
            <p className="text-xs text-muted">{t('abo.laden.verwaltenHinweis')}</p>
          )}
          {meldung && <p className="text-sm">{meldung}</p>}
        </Card>
      </div>
    </main>
  )
}
